import { Noise3 } from '../math/noise';

/**
 * A cave as a signed distance field: negative in the cave's air, positive in the rock.
 * Voids (ellipsoid chambers, and tubes made of round-cone segments) are joined by a
 * smooth union, rock is added back (bridges, pillars), and seeded noise roughens the
 * walls. Coordinates are cave-local metres (relative to the cave's anchor, so numbers
 * stay small). No three.js here: the cave worker runs this.
 *
 * Every void is also a little world of its own for gravity (see `pull`): it pulls you
 * onto its walls, strongest there and zero at its middle.
 */
export type V3 = [number, number, number];

/** How a stretch of wall looks: a tint (linear RGB multiplier) and a wetness, blended at junctions. */
export type Look = [number, number, number, number];

export interface EllipsoidVoid {
  kind: 'ellipsoid';
  center: V3;
  /** Orthonormal axes of the ellipsoid. */
  axes: [V3, V3, V3];
  radii: V3;
}

/** A tube along `points`, `radii[i]` wide at point i (round cones between neighbours). */
export interface TubeVoid {
  kind: 'tube';
  points: V3[];
  radii: number[];
}

/**
 * How a void pulls: `s` at its wall (m/s²), falling to zero at its middle as u^a, where u
 * is 0 in the middle and 1 at the wall (a = 1: the true, linear field of a hollow).
 */
export interface Pull {
  s: number;
  a: number;
}

/**
 * What grows on a void's walls, per square metre: stalagmites and boulders (rock, merged
 * into the walls' mesh), and glowing crystals and fungi (drawn apart, lighting the rock
 * around them in `glow`'s colour). See caveProps.ts.
 */
export interface PropRecipe {
  stalagmites?: number;
  boulders?: number;
  crystals?: number;
  fungi?: number;
  glow?: [number, number, number];
}

export type VoidShape = (EllipsoidVoid | TubeVoid) & {
  /** How far the walls bulge in and out (m). */
  rough: number;
  look: Look;
  pull?: Pull;
  props?: PropRecipe;
};

/** Rock put back into the air: a capsule from a to b. */
export interface RockShape {
  a: V3;
  b: V3;
  radius: number;
}

/** Where a cave meets an entrance: no roughness near it, so the two tubes line up. */
export interface Gate {
  p: V3;
}

export interface CaveShape {
  voids: VoidShape[];
  rock: RockShape[];
  /** Smooth-union radius where different voids meet (m). */
  blend: number;
  seed: number;
  gates?: Gate[];
}

const ELLIPSOID = 0;
const CONE = 1;
const ROCK = 2;
/** Floats per primitive in the packed table. */
const STRIDE = 16;
/** Wavelength of the wall roughness, per metre of roughness (gentle enough to walk on). */
const ROUGH_WAVELENGTH = 9;
/** Roughness is zero within GATE_CLEAR of a gate, full from GATE_ROUGH on. */
const GATE_CLEAR = 4;
const GATE_ROUGH = 10;
/** Spatial hash cell (m) for looking up what's near a point. */
const CELL = 8;

/** A box, cave-local. */
export interface Box {
  min: V3;
  max: V3;
}

/** The smooth field at a point, without the rock's bumps (for gravity and zones). */
export interface FieldSample {
  /** Distance (negative in the air). */
  phi: number;
  /** Blended pull at the wall, its profile, and the depth of the void's middle (m). */
  s: number;
  a: number;
  rho: number;
  /** The void that owns this point most (−1 far from every void). */
  owner: number;
}

/**
 * The field, with every void flattened into primitives (an ellipsoid, a tube segment, a
 * rock capsule) so a chunk of space, or a point, only looks at the few that can reach it.
 */
export class CaveSdf {
  readonly primitives: number;
  /** Which void each primitive belongs to (−1 for rock). */
  private readonly owner: Int32Array;
  private readonly kind: Uint8Array;
  private readonly data: Float64Array;
  private readonly boxes: Float64Array;
  private readonly noise: Noise3;
  private readonly rough: Float64Array;
  private readonly looks: Float64Array;
  private readonly pulls: Float64Array;
  private readonly gates: Float64Array;
  /** Largest roughness of any void: how far noise can move a wall. */
  readonly maxRough: number;
  private readonly hash = new Map<number, Int32Array>();
  // Scratch, reused by every sample.
  private readonly voidDist: Float64Array;
  private readonly voidRho: Float64Array;
  private readonly touched: Int32Array;
  private readonly all: number[];
  private lastRho = 0;
  private readonly _s: FieldSample = { phi: 0, s: 0, a: 1, rho: 1, owner: -1 };

  constructor(readonly shape: CaveShape) {
    const prims: { owner: number; kind: number; v: number[]; box: number[] }[] = [];
    const boxOf = (a: V3, b: V3, m: number) => [
      Math.min(a[0], b[0]) - m,
      Math.min(a[1], b[1]) - m,
      Math.min(a[2], b[2]) - m,
      Math.max(a[0], b[0]) + m,
      Math.max(a[1], b[1]) + m,
      Math.max(a[2], b[2]) + m,
    ];
    shape.voids.forEach((v, i) => {
      if (v.kind === 'ellipsoid') {
        const [c, [a0, a1, a2], r] = [v.center, v.axes, v.radii];
        const m = Math.max(r[0], r[1], r[2]);
        prims.push({ owner: i, kind: ELLIPSOID, v: [...c, ...a0, ...a1, ...a2, ...r], box: boxOf(c, c, m) });
      } else {
        for (let k = 0; k + 1 < v.points.length; k++) {
          const [a, b, ra, rb] = [v.points[k], v.points[k + 1], v.radii[k], v.radii[k + 1]];
          prims.push({ owner: i, kind: CONE, v: [...a, ...b, ra, rb], box: boxOf(a, b, Math.max(ra, rb)) });
        }
      }
    });
    for (const r of shape.rock)
      prims.push({ owner: -1, kind: ROCK, v: [...r.a, ...r.b, r.radius], box: boxOf(r.a, r.b, r.radius) });
    this.primitives = prims.length;
    this.owner = new Int32Array(prims.map((p) => p.owner));
    this.kind = new Uint8Array(prims.map((p) => p.kind));
    this.data = new Float64Array(prims.length * STRIDE);
    this.boxes = new Float64Array(prims.length * 6);
    prims.forEach((p, i) => {
      this.data.set(p.v, i * STRIDE);
      this.boxes.set(p.box, i * 6);
    });
    this.rough = new Float64Array(shape.voids.map((v) => v.rough));
    this.looks = new Float64Array(shape.voids.flatMap((v) => v.look));
    this.pulls = new Float64Array(shape.voids.flatMap((v) => [v.pull?.s ?? 0, v.pull?.a ?? 1]));
    this.gates = new Float64Array((shape.gates ?? []).flatMap((g) => g.p));
    this.maxRough = shape.voids.reduce((m, v) => Math.max(m, v.rough), 0);
    this.noise = new Noise3(shape.seed);
    this.voidDist = new Float64Array(shape.voids.length).fill(Number.POSITIVE_INFINITY);
    this.voidRho = new Float64Array(shape.voids.length);
    this.touched = new Int32Array(shape.voids.length);
    this.all = prims.map((_, i) => i);
    // Spatial hash: each cell lists the primitives whose reach overlaps it.
    const reach = shape.blend + this.maxRough + 1;
    const lists = new Map<number, number[]>();
    for (let i = 0; i < this.primitives; i++) {
      const o = i * 6;
      const lo = [0, 1, 2].map((a) => Math.floor((this.boxes[o + a] - reach) / CELL));
      const hi = [0, 1, 2].map((a) => Math.floor((this.boxes[o + 3 + a] + reach) / CELL));
      for (let z = lo[2]; z <= hi[2]; z++) {
        for (let y = lo[1]; y <= hi[1]; y++) {
          for (let x = lo[0]; x <= hi[0]; x++) {
            const key = cellKey(x, y, z);
            const list = lists.get(key);
            if (list) list.push(i);
            else lists.set(key, [i]);
          }
        }
      }
    }
    for (const [k, list] of lists) this.hash.set(k, Int32Array.from(list));
  }

  /** Bounds of every void (not the rock), grown by `margin`. */
  bounds(margin = 0): Box {
    const min: V3 = [Infinity, Infinity, Infinity];
    const max: V3 = [-Infinity, -Infinity, -Infinity];
    for (let i = 0; i < this.primitives; i++) {
      if (this.kind[i] === ROCK) continue;
      for (let a = 0; a < 3; a++) {
        min[a] = Math.min(min[a], this.boxes[i * 6 + a] - margin);
        max[a] = Math.max(max[a], this.boxes[i * 6 + 3 + a] + margin);
      }
    }
    return { min, max };
  }

  /** The primitives that can shape the field anywhere in `box` (for meshing it in chunks). */
  candidates(box: Box): number[] {
    const reach = this.shape.blend + this.maxRough + 1;
    const out: number[] = [];
    for (let i = 0; i < this.primitives; i++) {
      const o = i * 6;
      if (
        this.boxes[o] - reach <= box.max[0] &&
        this.boxes[o + 3] + reach >= box.min[0] &&
        this.boxes[o + 1] - reach <= box.max[1] &&
        this.boxes[o + 4] + reach >= box.min[1] &&
        this.boxes[o + 2] - reach <= box.max[2] &&
        this.boxes[o + 5] + reach >= box.min[2]
      ) {
        out.push(i);
      }
    }
    return out;
  }

  /** The primitives near a point (from the spatial hash); empty far from the cave. */
  near(x: number, y: number, z: number): ArrayLike<number> {
    return this.hash.get(cellKey(Math.floor(x / CELL), Math.floor(y / CELL), Math.floor(z / CELL))) ?? EMPTY;
  }

  /**
   * Signed distance at (x, y, z), bumps and all, using only `cands` (all primitives if
   * omitted). With `look`, the blended look of the walls there is written at
   * `look[at..at+3]`. Far from any wall (more than the roughness away) the noise is
   * skipped: the sign is all a mesher needs there.
   */
  distance(
    x: number,
    y: number,
    z: number,
    cands: ArrayLike<number> = this.all,
    look?: Float32Array,
    at = 0,
  ): number {
    const d = this.fold(x, y, z, cands, look, at);
    const amp = this._s.s; // (fold leaves the blended roughness here)
    if (amp <= 0 || Math.abs(d) > amp + 1) return d;
    let fade = 1;
    const g = this.gates;
    for (let i = 0; i < g.length; i += 3) {
      const r = Math.hypot(x - g[i], y - g[i + 1], z - g[i + 2]);
      fade = Math.min(fade, smooth01((r - GATE_CLEAR) / (GATE_ROUGH - GATE_CLEAR)));
    }
    if (fade <= 0) return d;
    const f = 1 / (ROUGH_WAVELENGTH * Math.max(amp, 0.2));
    // Two octaves: a third would be finer than the mesh can show.
    return d + amp * fade * this.noise.fbm(x * f, y * f, z * f, 2, 0.45);
  }

  /** The smooth field (no bumps) at a point, with its blended pull: for gravity and zones. */
  sample(x: number, y: number, z: number, out: FieldSample = this._s): FieldSample {
    const cands = this.near(x, y, z);
    const phi = this.fold(x, y, z, cands, undefined, 0, true);
    const s = this._s;
    out.phi = phi;
    if (out !== s) {
      out.s = s.s;
      out.a = s.a;
      out.rho = s.rho;
      out.owner = s.owner;
    }
    return out;
  }

  /**
   * Gravity at a point, from the smooth field: toward the nearest wall, with strength
   * s·u^a, where u runs from 0 in a void's middle to 1 at its wall. It also fades to zero
   * where opposite walls meet (the field's gradient vanishes there). Zero far from the cave.
   * Writes into `out` (x, y, z) and returns the strength.
   */
  gravity(x: number, y: number, z: number, out: { x: number; y: number; z: number }): number {
    const c = this.sample(x, y, z);
    if (c.owner < 0 || c.s <= 0) {
      out.x = out.y = out.z = 0;
      return 0;
    }
    const phi = c.phi;
    const s = c.s;
    const a = c.a;
    const rho = c.rho;
    // Gradient by a tetrahedron of samples (4 evaluations).
    const e = 0.25;
    const f1 = this.fold(x + e, y - e, z - e, this.near(x, y, z), undefined, 0, true);
    const f2 = this.fold(x - e, y - e, z + e, this.near(x, y, z), undefined, 0, true);
    const f3 = this.fold(x - e, y + e, z - e, this.near(x, y, z), undefined, 0, true);
    const f4 = this.fold(x + e, y + e, z + e, this.near(x, y, z), undefined, 0, true);
    const gx = (f1 - f2 - f3 + f4) / (4 * e);
    const gy = (-f1 - f2 + f3 + f4) / (4 * e);
    const gz = (-f1 + f2 - f3 + f4) / (4 * e);
    const len = Math.max(Math.hypot(gx, gy, gz), 0.35);
    const u = Math.min(Math.max(1 + phi / Math.max(rho, 0.5), 0), 1);
    const m = s * u ** a;
    out.x = (m * gx) / len;
    out.y = (m * gy) / len;
    out.z = (m * gz) / len;
    return m * (Math.hypot(gx, gy, gz) / len);
  }

  /**
   * The smooth union at a point: a hard minimum within each void (so a tube's joints
   * don't bulge), a smooth one across voids, rock put back. Leaves the blended
   * roughness (or, with `pull`, the blended pull) in `_s`.
   */
  private fold(
    x: number,
    y: number,
    z: number,
    cands: ArrayLike<number>,
    look: Float32Array | undefined,
    at: number,
    pull = false,
  ): number {
    const vd = this.voidDist;
    const vr = this.voidRho;
    const touched = this.touched;
    let nTouched = 0;
    let rockD = Number.POSITIVE_INFINITY;
    for (let c = 0; c < cands.length; c++) {
      const i = cands[c];
      const d = this.primitiveDistance(i, x, y, z);
      if (this.kind[i] === ROCK) {
        if (d < rockD) rockD = d;
        continue;
      }
      const v = this.owner[i];
      if (vd[v] === Number.POSITIVE_INFINITY) touched[nTouched++] = v;
      if (d < vd[v]) {
        vd[v] = d;
        vr[v] = this.lastRho;
      }
    }
    const s = this._s;
    if (nTouched === 0) {
      if (look) look.fill(0, at, at + 4);
      s.s = 0;
      s.a = 1;
      s.rho = 1;
      s.owner = -1;
      return 1e6;
    }
    const k = this.shape.blend;
    let v0 = touched[0];
    let d = vd[v0];
    let rho = vr[v0];
    let owner = v0;
    let ownW = 1;
    // Blended attributes: roughness (or pull strength and profile) and look.
    let q0 = pull ? this.pulls[v0 * 2] : this.rough[v0];
    let q1 = this.pulls[v0 * 2 + 1];
    let l0 = this.looks[v0 * 4];
    let l1 = this.looks[v0 * 4 + 1];
    let l2 = this.looks[v0 * 4 + 2];
    let l3 = this.looks[v0 * 4 + 3];
    vd[v0] = Number.POSITIVE_INFINITY;
    for (let t = 1; t < nTouched; t++) {
      v0 = touched[t];
      const b = vd[v0];
      vd[v0] = Number.POSITIVE_INFINITY;
      // h: the share of the running result (vs the new void) in the blend.
      const h = clamp01(0.5 + (0.5 * (b - d)) / k);
      d = b * (1 - h) + d * h - k * h * (1 - h);
      const w = 1 - h;
      rho += (vr[v0] - rho) * w;
      q0 += ((pull ? this.pulls[v0 * 2] : this.rough[v0]) - q0) * w;
      q1 += (this.pulls[v0 * 2 + 1] - q1) * w;
      if (look) {
        l0 += (this.looks[v0 * 4] - l0) * w;
        l1 += (this.looks[v0 * 4 + 1] - l1) * w;
        l2 += (this.looks[v0 * 4 + 2] - l2) * w;
        l3 += (this.looks[v0 * 4 + 3] - l3) * w;
      }
      // The owner is whichever void carries most of the blend so far.
      ownW *= h;
      if (w > ownW) {
        owner = v0;
        ownW = w;
      }
    }
    if (look) {
      look[at] = l0;
      look[at + 1] = l1;
      look[at + 2] = l2;
      look[at + 3] = l3;
    }
    // Rock put back: a smooth maximum with the rock's inside.
    if (rockD < Number.POSITIVE_INFINITY) {
      const r = -rockD;
      const kr = 0.6;
      const h = clamp01(0.5 - (0.5 * (r - d)) / kr);
      d = r * (1 - h) + d * h + kr * h * (1 - h);
    }
    s.s = q0;
    s.a = q1;
    s.rho = rho;
    s.owner = owner;
    return d;
  }

  /**
   * Distance to primitive i (negative inside a void; for rock, negative inside the rock).
   * Leaves in `lastRho` how deep that void's middle is there (its half-width).
   */
  private primitiveDistance(i: number, x: number, y: number, z: number): number {
    const o = i * STRIDE;
    const D = this.data;
    const kind = this.kind[i];
    if (kind === ELLIPSOID) {
      const px = x - D[o];
      const py = y - D[o + 1];
      const pz = z - D[o + 2];
      const qx = px * D[o + 3] + py * D[o + 4] + pz * D[o + 5];
      const qy = px * D[o + 6] + py * D[o + 7] + pz * D[o + 8];
      const qz = px * D[o + 9] + py * D[o + 10] + pz * D[o + 11];
      const rx = D[o + 12];
      const ry = D[o + 13];
      const rz = D[o + 14];
      this.lastRho = Math.min(rx, ry, rz);
      const k0 = Math.hypot(qx / rx, qy / ry, qz / rz);
      const k1 = Math.hypot(qx / (rx * rx), qy / (ry * ry), qz / (rz * rz));
      if (k1 < 1e-9) return -this.lastRho;
      return (k0 * (k0 - 1)) / k1;
    }
    const ax = D[o];
    const ay = D[o + 1];
    const az = D[o + 2];
    const bax = D[o + 3] - ax;
    const bay = D[o + 4] - ay;
    const baz = D[o + 5] - az;
    const pax = x - ax;
    const pay = y - ay;
    const paz = z - az;
    const t = clamp01(
      (pax * bax + pay * bay + paz * baz) / Math.max(bax * bax + bay * bay + baz * baz, 1e-9),
    );
    if (kind === ROCK) return Math.hypot(pax - bax * t, pay - bay * t, paz - baz * t) - D[o + 6];
    this.lastRho = D[o + 6] + (D[o + 7] - D[o + 6]) * t;
    return roundCone(pax, pay, paz, bax, bay, baz, D[o + 6], D[o + 7]);
  }
}

const EMPTY = new Int32Array(0);

function cellKey(x: number, y: number, z: number): number {
  // Cells within ±2^16 of the origin: plenty for a cave a few kilometres long.
  return ((x + 32768) * 65536 + (y + 32768)) * 65536 + (z + 32768);
}

function clamp01(x: number): number {
  return x < 0 ? 0 : x > 1 ? 1 : x;
}

function smooth01(x: number): number {
  const t = clamp01(x);
  return t * t * (3 - 2 * t);
}

/**
 * Distance to a round cone (a capsule whose radius goes from r1 at a to r2 at b), with
 * p − a = (px, py, pz) and b − a = (bx, by, bz). Inigo Quilez's exact formula.
 */
function roundCone(
  px: number,
  py: number,
  pz: number,
  bx: number,
  by: number,
  bz: number,
  r1: number,
  r2: number,
) {
  const l2 = bx * bx + by * by + bz * bz;
  if (l2 < 1e-12) return Math.hypot(px, py, pz) - Math.max(r1, r2);
  const rr = r1 - r2;
  const a2 = l2 - rr * rr;
  const il2 = 1 / l2;
  const y = px * bx + py * by + pz * bz;
  const zz = y - l2;
  const cx = px * l2 - bx * y;
  const cy = py * l2 - by * y;
  const cz = pz * l2 - bz * y;
  const x2 = cx * cx + cy * cy + cz * cz;
  const y2 = y * y * l2;
  const z2 = zz * zz * l2;
  const k = Math.sign(rr) * rr * rr * x2;
  if (Math.sign(zz) * a2 * z2 > k) return Math.sqrt(x2 + z2) * il2 - r2;
  if (Math.sign(y) * a2 * y2 < k) return Math.sqrt(x2 + y2) * il2 - r1;
  return (Math.sqrt(x2 * a2 * il2) + y * rr) * il2 - r1;
}
